import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import UploadFile from '@/components/UploadFile'
import CustomTextInput from '@/components/CustomTextInput'
import { type KycFormData, DocumentType } from '../types'

interface KycDocumentUploadFormProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  readonly?: boolean
  showHandheld?: boolean
}

export default function KycDocumentUploadForm({ 
  formData, 
  updateFormData, 
  readonly = false,
  showHandheld = false
}: KycDocumentUploadFormProps) {
  const { t } = useTranslation()
  const isIdCard = formData.identity_type === DocumentType.IdCard

  const [frontFile, setFrontFile] = useState<File | null>(null)
  const [backFile, setBackFile] = useState<File | null>(null)
  const [handheldFile, setHandheldFile] = useState<File | null>(null)

  // 处理上传成功
  const handleFrontUploadSuccess = useCallback((url: string) => {
    if (isIdCard) {
      updateFormData({ idCardFrontUrl: url })
    } else {
      updateFormData({ passportFrontUrl: url })
    }
  }, [isIdCard, updateFormData])

  const handleBackUploadSuccess = useCallback((url: string) => {
    updateFormData({ idCardBackUrl: url })
  }, [updateFormData])

  const handleHandheldUploadSuccess = useCallback((url: string) => {
    if (isIdCard) {
      updateFormData({ idCardHandheldUrl: url })
    } else {
      updateFormData({ passportHandheldUrl: url })
    }
  }, [isIdCard, updateFormData])

  // 处理删除图片
  const handleFrontRemove = useCallback(() => {
    if (isIdCard) {
      updateFormData({ idCardFrontUrl: '' })
    } else {
      updateFormData({ passportFrontUrl: '' })
    }
    setFrontFile(null)
  }, [isIdCard, updateFormData])

  const handleBackRemove = useCallback(() => {
    updateFormData({ idCardBackUrl: '' })
    setBackFile(null)
  }, [updateFormData])

  const handleHandheldRemove = useCallback(() => {
    if (isIdCard) {
      updateFormData({ idCardHandheldUrl: '' })
    } else {
      updateFormData({ passportHandheldUrl: '' })
    }
    setHandheldFile(null)
  }, [isIdCard, updateFormData])

  // 获取当前已上传的图片URL（用于查看模式）
  const getFrontUrl = () => isIdCard ? formData.idCardFrontUrl : formData.passportFrontUrl
  const getBackUrl = () => formData.idCardBackUrl
  const getHandheldUrl = () => isIdCard ? formData.idCardHandheldUrl : formData.passportHandheldUrl

  const getDocumentTypeLabel = () => {
    return isIdCard ? t('身份证') : t('护照')
  }

  return (
    <div className="space-y-6">
      {/* 证件类型（只读） */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('证件类型')}
        </div>
        <div className="w-full h-12 px-4 rounded-2.5 border border-solid border-[#EAECF0] bg-white flex items-center cursor-not-allowed opacity-60">
          <span className="text-[#1D2129]">{getDocumentTypeLabel()}</span>
        </div>
      </div>

      {/* 证件号（可编辑） */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('证件号')}
        </div>
        <CustomTextInput
          value={formData.identity_number}
          onChangeText={(val) => updateFormData({ identity_number: val })}
          placeholder={t('填写证件号码')}
          maxLength={25}
          disabled={readonly}
        />
      </div>

      {/* 身份证上传区域 */}
      {isIdCard ? (
        <>
          {/* 身份证正面 */}
          <div>
            <div className="text-base text-[#1D2129] font-medium mb-3">
              {t('身份证正面')}
            </div>
            {readonly && getFrontUrl() ? (
              <div className="aspect-[16/9.42] rounded-lg overflow-hidden border border-solid border-[#EAECF0]">
                <img src={getFrontUrl()} alt={t('身份证正面')} className="w-full h-full object-cover" />
              </div>
            ) : (
              <UploadFile
                value={frontFile}
                onChange={setFrontFile}
                onUploadSuccess={handleFrontUploadSuccess}
                onRemove={handleFrontRemove}
                placeholder="/images/my/idcard-placeholder-front.png"
                uploadedUrl={getFrontUrl()}
                autoUpload
                maxSize={2}
                className="aspect-[16/9.42]"
                uploadIcon="/images/my/icons/camera.svg"
              />
            )}
          </div>

          {/* 身份证背面 */}
          <div>
            <div className="text-base text-[#1D2129] font-medium mb-3">
              {t('身份证背面')}
            </div>
            {readonly && getBackUrl() ? (
              <div className="aspect-[16/9.42] rounded-lg overflow-hidden border border-solid border-[#EAECF0]">
                <img src={getBackUrl()} alt={t('身份证背面')} className="w-full h-full object-cover" />
              </div>
            ) : (
              <UploadFile
                value={backFile}
                onChange={setBackFile}
                onUploadSuccess={handleBackUploadSuccess}
                onRemove={handleBackRemove}
                placeholder="/images/my/idcard-placeholder-back.png"
                uploadedUrl={getBackUrl()}
                autoUpload
                maxSize={2}
                className="aspect-[16/9.42]"
                uploadIcon="/images/my/icons/camera.svg"
              />
            )}
          </div>

          {/* 手持证件照片 */}
          {showHandheld && (
            <div>
              <div className="text-base text-[#1D2129] font-medium mb-3">
                {t('手持证件照')}
              </div>
              {readonly && getHandheldUrl() ? (
                <div className="aspect-[16/9.42] rounded-lg overflow-hidden border border-solid border-[#EAECF0]">
                  <img src={getHandheldUrl()} alt={t('手持证件照')} className="w-full h-full object-cover" />
                </div>
              ) : (
                <UploadFile
                  value={handheldFile}
                  onChange={setHandheldFile}
                  onUploadSuccess={handleHandheldUploadSuccess}
                  onRemove={handleHandheldRemove}
                  placeholder="/images/my/handheld-placeholder.png"
                  uploadedUrl={getHandheldUrl()}
                  autoUpload
                  maxSize={2}
                  className="aspect-[16/9.42]"
                  uploadIcon="/images/my/icons/camera.svg"
                />
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* 护照个人信息页 */}
          <div>
            <div className="text-base text-[#1D2129] font-medium mb-3">
              {t('个人信息页')}
            </div>
            {readonly && getFrontUrl() ? (
              <div className="aspect-[16/19] rounded-lg overflow-hidden border border-solid border-[#EAECF0]">
                <img src={getFrontUrl()} alt={t('个人信息页')} className="w-full h-full object-cover" />
              </div>
            ) : (
              <UploadFile
                value={frontFile}
                onChange={setFrontFile}
                onUploadSuccess={handleFrontUploadSuccess}
                onRemove={handleFrontRemove}
                placeholder="/images/my/passport-placeholder.png"
                uploadedUrl={getFrontUrl()}
                autoUpload
                maxSize={2}
                className="aspect-[16/19]"
                uploadIcon="/images/my/icons/camera.svg"
              />
            )}
          </div>

          {/* 手持证件照片 */}
          {showHandheld && (
            <div>
              <div className="text-base text-[#1D2129] font-medium mb-3">
                {t('手持证件照')}
              </div>
              {readonly && getHandheldUrl() ? (
                <div className="aspect-[16/9.42] rounded-lg overflow-hidden border border-solid border-[#EAECF0]">
                  <img src={getHandheldUrl()} alt={t('手持证件照')} className="w-full h-full object-cover" />
                </div>
              ) : (
                <UploadFile
                  value={handheldFile}
                  onChange={setHandheldFile}
                  onUploadSuccess={handleHandheldUploadSuccess}
                  onRemove={handleHandheldRemove}
                  placeholder="/images/my/handheld-placeholder.png"
                  uploadedUrl={getHandheldUrl()}
                  autoUpload
                  maxSize={2}
                  className="aspect-[16/9.42]"
                  uploadIcon="/images/my/icons/camera.svg"
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
